import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getAssociations } from "../../../services/associationsServices";
import { getAllCommerces } from "../../../services/commercesServices";
import { getAllServicesMunicipaux } from "../../../services/servicesMunicipauxServices";

import {
  ensureArray,
  normalizeAssociationPoint,
  normalizeCommercePoint,
  normalizeSearchText,
  normalizeServicePoint,
  sortPoints,
} from "../utils/carteInteretsUtils";

const INITIAL_FILTERS = {
  q: "",
  types: {
    association: true,
    commerce: true,
    service: true,
  },
};

export function useCarteInteretsPage() {
  const bootedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [allPoints, setAllPoints] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [selectedPointId, setSelectedPointId] = useState(null);

  const deferredSearch = useDeferredValue(filters.q);

  const loadPoints = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [associations, commerces, services] = await Promise.all([
        getAssociations().catch(() => []),
        getAllCommerces().catch(() => []),
        getAllServicesMunicipaux().catch(() => []),
      ]);

      const associationPoints = ensureArray(associations)
        .filter((item) => item?.is_active !== false)
        .map(normalizeAssociationPoint)
        .filter(Boolean);

      const commercePoints = ensureArray(commerces)
        .filter((item) => item?.is_active !== false)
        .map(normalizeCommercePoint)
        .filter(Boolean);

      const servicePoints = ensureArray(services)
        .filter((item) => item?.is_active !== false && item?.is_public !== false)
        .map(normalizeServicePoint)
        .filter(Boolean);

      const rows = sortPoints([
        ...associationPoints,
        ...commercePoints,
        ...servicePoints,
      ]);

      setAllPoints(rows);
    } catch (err) {
      console.error("Erreur chargement points d’intérêt:", err);
      setAllPoints([]);
      setError("LOAD_ERROR");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    loadPoints();
  }, [loadPoints]);

  const filteredPoints = useMemo(() => {
    const q = normalizeSearchText(deferredSearch);

    return allPoints.filter((point) => {
      const matchType = filters.types[point.type];
      const matchSearch = q ? point.searchIndex.includes(q) : true;

      return matchType && matchSearch;
    });
  }, [allPoints, deferredSearch, filters.types]);

  const stats = useMemo(() => {
    return {
      total: allPoints.length,
      filtered: filteredPoints.length,
      association: allPoints.filter((point) => point.type === "association")
        .length,
      commerce: allPoints.filter((point) => point.type === "commerce").length,
      service: allPoints.filter((point) => point.type === "service").length,
    };
  }, [allPoints, filteredPoints.length]);

  const selectedPoint = useMemo(() => {
    return (
      filteredPoints.find((point) => point.id === selectedPointId) ||
      filteredPoints[0] ||
      null
    );
  }, [filteredPoints, selectedPointId]);

  useEffect(() => {
    if (!filteredPoints.length) {
      setSelectedPointId(null);
      return;
    }

    setSelectedPointId((current) => {
      const stillVisible = filteredPoints.some((point) => point.id === current);
      return stillVisible ? current : filteredPoints[0].id;
    });
  }, [filteredPoints]);

  const updateSearch = useCallback((value) => {
    setFilters((previous) => ({
      ...previous,
      q: value,
    }));
  }, []);

  const toggleType = useCallback((type) => {
    setFilters((previous) => ({
      ...previous,
      types: {
        ...previous.types,
        [type]: !previous.types[type],
      },
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setSelectedPointId(null);
  }, []);

  const selectPoint = useCallback((point) => {
    setSelectedPointId(point?.id || null);
  }, []);

  const refresh = useCallback(() => {
    setSelectedPointId(null);
    loadPoints();
  }, [loadPoints]);

  return {
    loading,
    error,

    points: filteredPoints,
    allPoints,
    selectedPoint,
    filters,
    stats,

    updateSearch,
    toggleType,
    resetFilters,
    selectPoint,
    refresh,
  };
}